import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import DestinationSelector from '../DestinationSelector';
import GuestsRoomsChildrenSelect from '../GuestsRoomsChildrenSelect';

import FullScreenScrollableDateRangeSelector from './FullScreenScrollableDateRangeSelector';

import type { AutosuggestSuggestionsOrPopularDestinations } from '../../types';
import type { I18nService } from '@shared/types/i18n';
import type { Maybe } from '@shared/types/utils';
import type { Destination } from '@shared/ui/types';

import STYLES from './HorizontalLayout.module.scss';

import BpkLoadingButton from '@shared/ui/common/BpkLoadingButton';
import { withI18n } from '@shared/ui/contexts/i18n';

const cls = cssModules(STYLES);

type Props = {
  i18n: I18nService;
  destination?: Destination;
  checkInDate: Maybe<Date>;
  checkOutDate: Maybe<Date>;
  destinationLabel: string;
  childrenAges: number[];
  adults: number;
  rooms: number;
  suggestions: AutosuggestSuggestionsOrPopularDestinations;
  onChangeInputValue: Function;
  onClearSuggestions: Function;
  onSuggestionSelected: Function;
  onSuggestionsFetchRequested: Function;
  onDatesRangeApply: Function;
  getGuestsRoomsChildren: Function;
  lightLabel?: boolean;
  validDestination: Maybe<boolean>;
  submittingForm?: boolean;
  showSearchButton: boolean;
  arrangeInline?: boolean;
  hideLabel?: boolean;
};

const HorizontalLayout = ({
  adults,
  arrangeInline,
  checkInDate,
  checkOutDate,
  childrenAges,
  destination,
  destinationLabel,
  getGuestsRoomsChildren,
  hideLabel,
  i18n: { translate },
  lightLabel,
  onChangeInputValue,
  onClearSuggestions,
  onDatesRangeApply,
  onSuggestionSelected,
  onSuggestionsFetchRequested,
  rooms,
  showSearchButton,
  submittingForm,
  suggestions,
  validDestination,
}: Props) => (
  <>
    <div className={cls('HorizontalLayout')}>
      <DestinationSelector
        destinationLabel={destinationLabel}
        destination={destination}
        lightLabel={lightLabel}
        inputClassName={cls('HorizontalLayout__input')}
        onSuggestionSelected={onSuggestionSelected}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onClearSuggestions}
        suggestions={suggestions}
        onChangeInputValue={onChangeInputValue}
        valid={validDestination}
        hideLabel={hideLabel}
      />

      <div
        className={cls('HorizontalLayout__datesGuest')}
        data-test-id="search-controls-datesguest"
      >
        <FullScreenScrollableDateRangeSelector
          startDate={checkInDate}
          endDate={checkOutDate}
          onApply={onDatesRangeApply}
          dateRangeClassName={
            arrangeInline && cls('HorizontalLayout__dateRange')
          }
        />
        <GuestsRoomsChildrenSelect
          className={cls(
            'HorizontalLayout__guestsRoomsChildren',
            arrangeInline &&
              'HorizontalLayout__guestsRoomsChildren--arrangeInline',
          )}
          lightLabel={lightLabel}
          adults={adults}
          rooms={rooms}
          childrenAges={childrenAges}
          getGuestsRoomsChildren={getGuestsRoomsChildren}
          hideLabel={hideLabel}
        />
      </div>
      {showSearchButton && arrangeInline && (
        <BpkLoadingButton
          iconOnly
          className={cls('HorizontalLayout__searchButton')}
          loading={submittingForm}
          submit
          featured
          data-test-id="search-button"
        />
      )}
    </div>
    {showSearchButton && !arrangeInline && (
      <BpkLoadingButton
        className={cls('HorizontalLayout__button')}
        loading={submittingForm}
        submit
        large
        featured
        data-test-id="search-button"
      >
        <span>{translate('SearchControls_label_Submit')}</span>
      </BpkLoadingButton>
    )}
  </>
);

export default withI18n(HorizontalLayout);
